import React, { useState } from 'react';
import WeatherData from '../../core/WeatherData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudRain, faDroplet, faTemperatureQuarter, faWater, faUpLong, faDownLong, faStar, faChartArea, faTableColumns } from '@fortawesome/free-solid-svg-icons';
import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';
import './CityCard.css';
import RainChartComponent from './charts/RainChart';
import RiverChartComponent from './charts/RiverChart';
import TemperatureChartComponent from './charts/TemperatureChart';

interface CityCardProps {
    weatherData: WeatherData;
    isFavorite: boolean;
    favoriteCallback: (cityName: string, isFavorite: boolean) => void;
}

const CityCardComponent: React.FC<CityCardProps> = ({ weatherData, isFavorite, favoriteCallback }) => {
    const [showCurrent, setShowCurrent] = useState(true);
    const [riverChart, setRiverChart] = useState(true);
    const [rainChart, setRainChart] = useState(false);
    const [temperatureChart, setTemperatureChart] = useState(false);

    const toggleFavorite = () => {
        favoriteCallback(weatherData.getName(), !isFavorite);
    };

    const toggleCurrentView = () => {
        setShowCurrent(!showCurrent);
    };

    const showRiverChart = () => {
        setRiverChart(true);
        setRainChart(false);
        setTemperatureChart(false);
    };

    const showRainChart = () => {
        setRiverChart(false);
        setRainChart(true);
        setTemperatureChart(false);
    };

    const showTemperatureChart = () => {
        setTemperatureChart(true);
        setRiverChart(false);
        setRainChart(false);
    };

    const rain = weatherData.getCurrentRain();
    const humidity = weatherData.getHumidity();
    const riverLevel = weatherData.getRiverLevel();
    const temperature = weatherData.getTemperature();
    const temperatureFeel = weatherData.getTemperatureFeel();
    let levelChange = weatherData.getLevelDifference();
    let levelDiff;
    if (levelChange !== null) {
        const levelIcon = levelChange > 0 ? <FontAwesomeIcon color='red' icon={faUpLong}></FontAwesomeIcon> : <FontAwesomeIcon color='green' icon={faDownLong}></FontAwesomeIcon>;
        levelDiff = <div className="level-diff">{levelIcon}<span>{levelChange.toFixed(2)} m</span></div>
    } else {
        levelDiff = <span />
    }
    const displayIcon = showCurrent ? faChartArea : faTableColumns;
    const icon = isFavorite ? faStar : farStar;

    // Helper function to render loading state or value
    const renderValue = (value: number | null, unit: string, isLoaded: boolean) => {
        if (!isLoaded || value === null || value === undefined) {
            return <span className="loading-shimmer"></span>;
        }
        return `${value.toFixed(value % 1 === 0 ? 0 : 1)}${unit}`;
    };

    const renderTemperatureValue = (value: number | null, unit: string, isLoaded: boolean, decimals: number = 1) => {
        if (!isLoaded || value === null || value === undefined) {
            return <span className="loading-shimmer small"></span>;
        }
        return `${value.toFixed(decimals)}${unit}`;
    };

    let data;
    if (showCurrent) {
        data = <div><div className="metrics columns is-mobile">
            <div className="column is-half metric rain-container">
                <div className="is-flex is-flex-direction-column">
                    <FontAwesomeIcon color='gray' size='2x' icon={faCloudRain} />
                    <span>{renderValue(rain, ' mm', weatherData.isLoaded())}</span>
                </div>
            </div>
            <div className="column is-half metric humidity-container">
                <div className="is-flex is-flex-direction-column">
                    <FontAwesomeIcon color='blue' size='2x' icon={faDroplet} />
                    <span>{renderValue(humidity, ' %', weatherData.isLoaded())}</span>
                </div>
            </div>
        </div>
            <div className="metrics columns is-mobile">
                <div className="column is-half metric temperature-container">
                    <div className="is-flex is-flex-direction-column">
                        <FontAwesomeIcon color='orange' size='2x' icon={faTemperatureQuarter} />
                        <span>{renderTemperatureValue(temperature, ' ºC', weatherData.isLoaded())}</span>
                        <span className="is-size-7">{renderTemperatureValue(temperatureFeel, ' ºC', weatherData.isLoaded())}</span>
                        <span className="is-size-7">(sensação)</span>
                    </div>
                </div>
                <div className="column is-half metric river-container">
                    <div className="is-flex is-flex-direction-column">
                        <FontAwesomeIcon color='lightblue' size='2x' icon={faWater} />
                        <span>{renderValue(riverLevel, ' m', weatherData.isLoaded())}</span>
                        {levelDiff}
                    </div>
                </div>
            </div></div>
    } else {
        const riverClass = riverChart ? 'is-link is-light is-active' : '';
        const rainClass = rainChart ? 'is-link is-light is-active' : '';
        const temperatureClass = temperatureChart ? 'is-link is-light is-active' : '';
        const chartTypeToggle =
            (<div className="chart-type">
                <button className={`button is-small ${riverClass}`} onClick={showRiverChart}><FontAwesomeIcon color='blue' icon={faWater} /></button>
                <button className={`button is-small ${rainClass}`} onClick={showRainChart}><FontAwesomeIcon color='gray' icon={faCloudRain} /></button>
                <button className={`button is-small ${temperatureClass}`} onClick={showTemperatureChart}><FontAwesomeIcon color='orange' icon={faTemperatureQuarter} /></button>
            </div>)
        if (riverChart) {
            const levels = weatherData.getRiverLevels();     
            data = <div>
                <div className='chart-title'><FontAwesomeIcon color='lightblue' icon={faWater} /><span>Nível do Rio</span></div>
                <RiverChartComponent weatherData={weatherData} />
                {chartTypeToggle}
            </div>                
        } else if (rainChart) {
            const volumes = weatherData.getRainVolumes();
            const chartData = volumes?.map((l: any, i: number) => { return { x: `${l.getPeriod()}h`, y: l.getVolume() }; });
            data = <div>
                <div className='chart-title'><FontAwesomeIcon color='gray' icon={faCloudRain} /><span>Chuva (mm)</span></div>
                <RainChartComponent weatherData={weatherData} />
                {chartTypeToggle}
            </div>
        } else if (temperatureChart) {                
            data = (<div>
                <div className='chart-title'><FontAwesomeIcon color='orange' icon={faTemperatureQuarter} /><span>Temperatura (ºC)</span></div>
                <TemperatureChartComponent weatherData={weatherData} />
                {chartTypeToggle}
            </div>)
        }
    }

    return (
        <div className="column is-one-third">
            <div className="box">
                <div className="city-header">
                    <FontAwesomeIcon color='darkSlateBlue' className="toggle-current" icon={displayIcon} onClick={toggleCurrentView}></FontAwesomeIcon>
                    <div className="city-name is-size-4">{weatherData.getName()}</div>
                    <FontAwesomeIcon color='gold' className="toggle-favorite" icon={icon} onClick={toggleFavorite}></FontAwesomeIcon>
                </div>
                {data}
            </div>
        </div>
    )
};

export default CityCardComponent;
